import React, { useState, useEffect } from 'react';
import './EmployerSection.css';

const COMPANY_SIZES = ['', '1-50', '51-200', '201-500', '500+'];
const HIRING_TYPES = ['', 'Full-time', 'Contract', 'Temp', 'Project-based', 'Mix'];
const TIMELINES = ['', 'Urgent', '1-3 months', '3-6 months'];
const SERVICE_INTERESTS = ['', 'Permanent staffing', 'Contract staffing', 'Executive search', 'RPO', 'Other'];
const HEAR_ABOUT = ['', 'Google', 'LinkedIn', 'Referral', 'Website', 'Other'];
const PREFER_TIME_TO_CALL = ['', 'Morning (9am-12pm)', 'Afternoon (12pm-5pm)', 'Evening (5pm-8pm)', 'Anytime'];

const EMPLOYER_BENEFITS = ['Integrity', 'Commitment', 'Teamwork', 'Respect', 'Professionalism', 'Courage'];

const EmployerSection = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const employerEndpoint = `${apiUrl}/api/contact/employer`;

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    jobTitle: '',
    industry: '',
    companySize: '',
    hiringType: '',
    timeline: '',
    serviceInterest: '',
    location: '',
    numPositions: '',
    hiringNeed: '',
    message: '',
    hearAbout: '',
    preferTimeToCall: '',
  });

  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    companyDetails: false,
    hiringNeeds: false,
    additionalInfo: false,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const phoneRegex = /^\+?(\d[\d\s-()]{7,}\d)$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/industry/list?minimal=true`);
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) {
          setIndustries(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch industries:', err);
      } finally {
        setIndustriesLoading(false);
      }
    };
    fetchIndustries();
  }, [apiUrl]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    const companyTrim = (formData.companyName || '').trim();
    const contactTrim = (formData.contactPerson || '').trim();
    const emailTrim = (formData.email || '').trim();
    const phoneTrim = (formData.phone || '').trim();

    if (!companyTrim) newErrors.companyName = 'Company name is required.';
    if (!contactTrim) newErrors.contactPerson = 'Contact person is required.';
    if (!emailTrim) newErrors.email = 'Email is required.';
    else if (!emailRegex.test(emailTrim)) newErrors.email = 'Please enter a valid email address.';
    if (phoneTrim && !phoneRegex.test(phoneTrim)) newErrors.phone = 'Please enter a valid phone number.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationResult = validateForm();
    if (validationResult) {
      setError('Please fill in all required fields.');
      return;
    }
    try {
      const response = await fetch(employerEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      });
      let data = {};
      try {
        data = await response.json();
      } catch {
        // Response may not be JSON
      }
      if (response.ok && data.success) {
        setIsSubmitted(true);
        setErrors({});
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          jobTitle: '',
          industry: '',
          companySize: '',
          hiringType: '',
          timeline: '',
          serviceInterest: '',
          location: '',
          numPositions: '',
          hiringNeed: '',
          message: '',
          hearAbout: '',
          preferTimeToCall: '',
        });
      } else {
        setError(data?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting employer form:', err);
      setError(err?.message || 'An error occurred. Please try again.');
    }
  };

  const FormField = ({ id, label, required, children, error, fullWidth }) => (
    <div className={`form-field ${fullWidth ? 'full-width' : ''}`}>
      <label htmlFor={id} className="form-label">
        {label} {required && <span className="form-required">*</span>}
      </label>
      {children}
      {error && <span className="error-message">{error}</span>}
    </div>
  );

  return (
    <div className="EmployerSection ContactSession" id="employer-section">
      <div className="hbar">
        <span>Employers</span>
        <h1>Partner with our industry expert for top talent</h1>
      </div>

      <div className="container">
        <div className="card">
          <div className="mb-8">
            <h1 className="section-title">Ready to hire?</h1>
            <p className="section-subtitle">
              Partner with us for staffing, recruitment, and workforce solutions across industries. Submit the form and we&apos;ll respond within 24 hours.
            </p>
          </div>

          <div className="mb-8">
            <p className="call-us-title">Phone:</p>
            <p className="phone-number">+91 9650481240</p>
            <p className="call-us-title">Email:</p>
            <p className="phone-number">info@adamanthr.com</p>
          </div>

          <div>
            <h2 className="section-title mb-4">Our values:</h2>
            <div className="benefits-grid">
              {EMPLOYER_BENEFITS.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="benefit-icon">
                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 0 0-1.22-.872l-3.236 4.53L9.53 12.293a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                  </svg>
                  <span className="benefit-text">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="form-container" id="form-container">
          <div className="header">
            <h1 className="header-title">Employer Inquiry</h1>
            <div className="header-line"></div>
            <img src="/images/arrow.png" alt="" className="arrow-down" />
            <div className="header-line"></div>
          </div>

          {error && (
            <div className="error-message mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <form className="form-grid" onSubmit={handleSubmit}>
            <FormField id="companyName" label="Company name" required error={errors.companyName}>
              <input type="text" id="companyName" className="form-input" value={formData.companyName} onChange={handleChange} placeholder="Your company" />
            </FormField>
            <FormField id="contactPerson" label="Contact person" required error={errors.contactPerson}>
              <input type="text" id="contactPerson" className="form-input" value={formData.contactPerson} onChange={handleChange} placeholder="Your name" />
            </FormField>
            <FormField id="email" label="Email" required error={errors.email} fullWidth>
              <input type="email" id="email" className="form-input" value={formData.email} onChange={handleChange} placeholder="you@company.com" />
            </FormField>
            <FormField id="phone" label="Phone" error={errors.phone} fullWidth>
              <input type="tel" id="phone" className="form-input" value={formData.phone} onChange={handleChange} placeholder="+91 9876543210" />
            </FormField>

            {/* Optional: Company details */}
            <div className={`form-collapse ${isMobile && !expandedSections.companyDetails ? 'form-collapse--closed' : ''}`}>
              <button type="button" className="form-collapse-header" onClick={() => isMobile && toggleSection('companyDetails')}>
                <span className="form-collapse-icon">{(!isMobile || expandedSections.companyDetails) ? '−' : '+'}</span>
                Company details
              </button>
              <div className="form-collapse-content">
                <FormField id="jobTitle" label="Job title">
                  <input type="text" id="jobTitle" className="form-input" value={formData.jobTitle} onChange={handleChange} placeholder="e.g. HR Manager" />
                </FormField>
                <FormField id="industry" label="Industry">
                  <select id="industry" className="form-input" value={formData.industry} onChange={handleChange} disabled={industriesLoading}>
                    <option value="">{industriesLoading ? 'Loading...' : 'Select industry'}</option>
                    {industries.map((ind) => <option key={ind._id} value={ind.name}>{ind.name}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </FormField>
                <FormField id="companySize" label="Company size">
                  <select id="companySize" className="form-input" value={formData.companySize} onChange={handleChange}>
                    {COMPANY_SIZES.map((opt) => <option key={opt || 'empty'} value={opt}>{opt || 'Select size'}</option>)}
                  </select>
                </FormField>
                <FormField id="location" label="Location">
                  <input type="text" id="location" className="form-input" value={formData.location} onChange={handleChange} placeholder="City or Remote" />
                </FormField>
              </div>
            </div>

            {/* Optional: Hiring needs */}
            <div className={`form-collapse ${isMobile && !expandedSections.hiringNeeds ? 'form-collapse--closed' : ''}`}>
              <button type="button" className="form-collapse-header" onClick={() => isMobile && toggleSection('hiringNeeds')}>
                <span className="form-collapse-icon">{(!isMobile || expandedSections.hiringNeeds) ? '−' : '+'}</span>
                Hiring needs
              </button>
              <div className="form-collapse-content">
                <FormField id="hiringType" label="Type of hiring">
                  <select id="hiringType" className="form-input" value={formData.hiringType} onChange={handleChange}>
                    {HIRING_TYPES.map((opt) => <option key={opt || 'empty'} value={opt}>{opt || 'Select type'}</option>)}
                  </select>
                </FormField>
                <FormField id="timeline" label="Timeline">
                  <select id="timeline" className="form-input" value={formData.timeline} onChange={handleChange}>
                    {TIMELINES.map((opt) => <option key={opt || 'empty'} value={opt}>{opt || 'Select timeline'}</option>)}
                  </select>
                </FormField>
                <FormField id="serviceInterest" label="Service interest">
                  <select id="serviceInterest" className="form-input" value={formData.serviceInterest} onChange={handleChange}>
                    {SERVICE_INTERESTS.map((opt) => <option key={opt || 'empty'} value={opt}>{opt || 'Select service'}</option>)}
                  </select>
                </FormField>
                <FormField id="numPositions" label="Number of positions">
                  <input type="text" id="numPositions" className="form-input" value={formData.numPositions} onChange={handleChange} placeholder="e.g. 5" />
                </FormField>
                <div className="form-field full-width">
                  <label htmlFor="hiringNeed" className="form-label">Hiring need</label>
                  <textarea id="hiringNeed" className="form-textarea" value={formData.hiringNeed} onChange={handleChange} placeholder="Brief description of roles and requirements..." rows={4} />
                </div>
              </div>
            </div>

            {/* Optional: Additional info */}
            <div className={`form-collapse ${isMobile && !expandedSections.additionalInfo ? 'form-collapse--closed' : ''}`}>
              <button type="button" className="form-collapse-header" onClick={() => isMobile && toggleSection('additionalInfo')}>
                <span className="form-collapse-icon">{(!isMobile || expandedSections.additionalInfo) ? '−' : '+'}</span>
                Additional info
              </button>
              <div className="form-collapse-content">
                <FormField id="hearAbout" label="How did you hear about us?">
                  <select id="hearAbout" className="form-input" value={formData.hearAbout} onChange={handleChange}>
                    {HEAR_ABOUT.map((opt) => <option key={opt || 'empty'} value={opt}>{opt || 'Select'}</option>)}
                  </select>
                </FormField>
                <FormField id="preferTimeToCall" label="Prefer time to call">
                  <select id="preferTimeToCall" className="form-input" value={formData.preferTimeToCall} onChange={handleChange}>
                    {PREFER_TIME_TO_CALL.map((opt) => <option key={opt || 'empty'} value={opt}>{opt || 'Select'}</option>)}
                  </select>
                </FormField>
                <div className="form-field full-width">
                  <label htmlFor="message" className="form-label">Additional message</label>
                  <textarea id="message" className="form-textarea" value={formData.message} onChange={handleChange} placeholder="Any other details or questions..." rows={3} />
                </div>
              </div>
            </div>

            <div className="full-width">
              <button type="submit" className="submit-button">Submit</button>
            </div>
          </form>

          {isSubmitted && <div className="success-message">Thank you! Your inquiry has been sent. We will get back to you soon.</div>}
        </div>
      </div>
    </div>
  );
};

export default EmployerSection;